/**
 * GuidedPricing (06/2026) — geführter Preis-Dialog als Alternative zur klassischen
 * Preisliste (Umschalter im Admin, global). Fragt den Kunden Schritt für Schritt:
 *   1. Was möchtest du machen?  (Service-Kategorie)
 *   2. Alleine / zu zweit / zu dritt?  (konkrete Variante 1:1 / 1:2 / 1:3)
 *   3. Wie oft pro Woche?
 *   4. Ergebnis: Einzelpreis + 25%-Vorteil ab dem 2. Training/Woche
 *      + günstigste Option (Punkte-Pakete & Abo gegenübergestellt).
 *
 * Die 25%-Regel (Marketing) gilt ab dem 2. Training pro Woche — auf CHF UND Punkte.
 */
import React, { useState } from 'react';
import { ArrowRight, ArrowLeft, RotateCcw, Sparkles, Users, User, CalendarDays, Check, Star, Package, Trophy, TrendingDown } from 'lucide-react';

const chf = (v) => {
  const n = Number(v) || 0;
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};
const groupLabel = (maxP) => (maxP >= 3 ? 'Zu dritt (1:3)' : maxP === 2 ? 'Zu zweit (1:2)' : 'Einzeln (1:1)');
const GroupIcon = ({ maxP, ...p }) => (maxP > 1 ? <Users {...p} /> : <User {...p} />);

const GuidedPricing = ({
  categories = [], pointPackages = [], creditPackages = [],
  currency = 'CHF', discountPct = 25, theme, headingFont, fontFamily,
  onBook, onBuyPackage, isEmbed = false,
  basePricePerPoint = 9.375, pointsPricingMode = 'bonus',
  pointTiers = [], serviceAboTiers = [],
}) => {
  const C = theme;
  const [step, setStep] = useState('category');
  const [category, setCategory] = useState(null);
  const [service, setService] = useState(null);
  const [frequency, setFrequency] = useState(2);

  // Kategorien vorbereiten: Probesessions/leere raus, nur Services mit Preis
  const cats = categories
    .map((c) => ({ name: c.name, services: (c.services || []).filter((s) => Number(s.price) > 0) }))
    .filter((c) => c.services.length > 0 && !/probe/i.test(c.name));

  const reset = () => { setStep('category'); setCategory(null); setService(null); setFrequency(2); };

  const pickCategory = (c) => {
    setCategory(c);
    if (c.services.length === 1) { setService(c.services[0]); setStep('frequency'); }
    else { setService(null); setStep('variant'); }
  };
  const pickService = (s) => { setService(s); setStep('frequency'); };
  const pickFrequency = (f) => { setFrequency(f); setStep('result'); };

  // ---------- Styles ----------
  const btnBase = {
    fontFamily: headingFont, cursor: 'pointer', transition: 'all .2s ease',
    borderRadius: 14, border: `1px solid ${C.border}`, background: C.cardBg, color: C.text,
  };
  const optionCard = (active) => ({
    ...btnBase, padding: isEmbed ? '16px 14px' : '22px 20px', textAlign: 'left',
    display: 'flex', alignItems: 'center', gap: 14,
    borderColor: active ? C.accent : C.border,
    boxShadow: active ? `0 0 0 1px ${C.accent}` : 'none',
  });
  const iconWrap = {
    width: 42, height: 42, borderRadius: 12, flexShrink: 0,
    background: 'rgba(234,179,8,0.12)', border: `1px solid ${C.border}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.accent,
  };
  const stepTitle = { fontFamily: headingFont, fontSize: isEmbed ? 20 : 26, fontWeight: 700, color: C.text, margin: '0 0 4px', letterSpacing: '-0.02em' };
  const stepSub = { color: C.textMuted, fontSize: 13, margin: '0 0 22px' };
  const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 };

  const StepHeader = ({ n, title, sub, onBack }) => (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {['category', 'variant', 'frequency', 'result'].map((s, i) => (
            <span key={s} style={{
              width: 26, height: 4, borderRadius: 4,
              background: (['category', 'variant', 'frequency', 'result'].indexOf(step) >= i) ? C.accent : C.border,
            }} />
          ))}
        </div>
        {onBack && (
          <button onClick={onBack} data-testid="guided-back" style={{
            ...btnBase, borderRadius: 999, padding: '6px 12px', fontSize: 12,
            display: 'inline-flex', alignItems: 'center', gap: 6, color: C.textMuted,
          }}><ArrowLeft size={14} /> Zurück</button>
        )}
      </div>
      <h2 style={stepTitle}>{title}</h2>
      {sub && <p style={stepSub}>{sub}</p>}
    </div>
  );

  // ---------- Step 1: Kategorie ----------
  if (step === 'category') {
    return (
      <div data-testid="guided-pricing" style={{ maxWidth: 760, margin: '0 auto' }}>
        <StepHeader title="Was möchtest du machen?" sub="Wähle deinen Trainingsbereich – wir zeigen dir gleich den besten Preis." />
        <div style={grid}>
          {cats.map((c) => (
            <button key={c.name} onClick={() => pickCategory(c)} style={optionCard(false)}
              data-testid={`guided-cat-${c.name}`}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = C.accent)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = C.border)}>
              <span style={iconWrap}><Sparkles size={20} /></span>
              <span>
                <span style={{ display: 'block', fontWeight: 700, fontSize: 15 }}>{c.name}</span>
                <span style={{ display: 'block', color: C.textMuted, fontSize: 12, marginTop: 2 }}>
                  ab {currency} {chf(Math.min(...c.services.map((s) => Number(s.price))))}
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ---------- Step 2: Variante (Gruppengröße) ----------
  if (step === 'variant') {
    return (
      <div data-testid="guided-pricing" style={{ maxWidth: 760, margin: '0 auto' }}>
        <StepHeader title="Alleine oder gemeinsam?" sub={`${category.name} – wähle deine Variante.`} onBack={() => setStep('category')} />
        <div style={grid}>
          {category.services.map((s) => (
            <button key={s.id} onClick={() => pickService(s)} style={optionCard(false)}
              data-testid={`guided-variant-${s.id}`}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = C.accent)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = C.border)}>
              <span style={iconWrap}><GroupIcon maxP={s.max_participants} size={20} /></span>
              <span>
                <span style={{ display: 'block', fontWeight: 700, fontSize: 15 }}>{groupLabel(s.max_participants)}</span>
                <span style={{ display: 'block', color: C.textMuted, fontSize: 12, marginTop: 2 }}>
                  {s.name}{s.duration_minutes > 0 ? ` · ${s.duration_minutes} Min` : ''} · {currency} {chf(s.price)}
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ---------- Step 3: Häufigkeit ----------
  if (step === 'frequency') {
    return (
      <div data-testid="guided-pricing" style={{ maxWidth: 760, margin: '0 auto' }}>
        <StepHeader title="Wie oft pro Woche?" sub={`${service.name} – ab dem 2. Training pro Woche sparst du ${discountPct}%.`}
          onBack={() => setStep(category.services.length === 1 ? 'category' : 'variant')} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 12 }}>
          {[1, 2, 3, 4, 5].map((f) => (
            <button key={f} onClick={() => pickFrequency(f)} style={{ ...optionCard(frequency === f), flexDirection: 'column', alignItems: 'center', gap: 6, padding: '20px 10px', textAlign: 'center' }}
              data-testid={`guided-freq-${f}`}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = C.accent)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = C.border)}>
              <CalendarDays size={20} style={{ color: C.accent }} />
              <span style={{ fontWeight: 800, fontSize: 22 }}>{f}×</span>
              <span style={{ color: C.textMuted, fontSize: 11 }}>pro Woche</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ---------- Step 4: Ergebnis ----------
  const factor = 1 - discountPct / 100;
  const price = Number(service.price) || 0;
  const pts = Number(service.points_cost) || 0;
  const f = frequency;
  const WEEKS = 4;

  // 25%-Regel: 1. Training voll, jedes weitere pro Woche -25% (CHF & Punkte)
  const secondPrice = price * factor;
  const secondPts = Math.round(pts * factor);
  const weeklyFull = price * f;
  const weeklyOffer = price + (f - 1) * secondPrice;
  const weeklySavings = weeklyFull - weeklyOffer;
  const monthlyOffer = weeklyOffer * WEEKS;
  const monthlySessions = f * WEEKS;
  const monthlyPts = WEEKS * (pts + (f > 1 ? Math.round((f - 1) * pts * factor) : 0));

  // Tarif-Stufen finden (gleiche Logik wie Backend pricing_calculator)
  const findTier = (qty, tiers) => (tiers || []).find((t) => {
    const mn = Number(t.min_points) || 0;
    const mx = t.max_points == null ? Infinity : Number(t.max_points);
    return qty >= mn && qty <= mx;
  }) || null;

  // Service-Abo (Mengenrabatt) für eine bestimmte Sessionzahl
  const aboFor = (qty) => {
    const t = findTier(qty, serviceAboTiers);
    const pct = t ? Number(t.discount_percent) || 0 : 0;
    const total = qty * price * (1 - pct / 100);
    return { pct, label: t?.label || '', total, perSession: qty > 0 ? total / qty : 0 };
  };
  const abo = aboFor(monthlySessions);

  // Punkte-Paket (Bonus-Modus): monatl. Punkte auf einmal kaufen
  const pTier = findTier(monthlyPts, pointTiers);
  const bonusPct = pTier ? Number(pTier.discount_percent) || 0 : 0;
  const bonusPoints = Math.round(monthlyPts * bonusPct / 100);
  const pointsFullCHF = monthlyPts * basePricePerPoint;
  const pointsBonusValue = bonusPoints * basePricePerPoint;
  const pointsEffCHF = pointsPricingMode === 'bonus'
    ? pointsFullCHF - pointsBonusValue           // Bonus ≙ geschenkter Wert
    : pointsFullCHF * (1 - bonusPct / 100);

  // Sparziel-Hinweis: ab welcher Häufigkeit schlägt ein Abo die Einzelzahlung (mit 25%)?
  const singleMonthly = (freq) => WEEKS * (price + (freq - 1) * secondPrice);
  let aboThreshold = null;
  for (let ff = 1; ff <= 6; ff += 1) {
    if (aboFor(ff * WEEKS).total < singleMonthly(ff) - 0.5) { aboThreshold = ff; break; }
  }

  const card = { background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 16, padding: isEmbed ? 16 : 20 };
  const cta = (solid) => ({
    ...btnBase, width: '100%', padding: '13px 16px', borderRadius: 10, fontWeight: 700,
    fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    background: solid ? C.accent : C.bg, color: solid ? '#000' : C.text,
    borderColor: solid ? C.accent : C.border,
  });

  return (
    <div data-testid="guided-pricing" style={{ maxWidth: 820, margin: '0 auto' }}>
      <StepHeader title="Dein persönlicher Preis" sub={`${service.name} · ${f}× pro Woche`} onBack={() => setStep('frequency')} />

      {/* 25%-Vorteil */}
      <div style={{ ...card, borderColor: `${C.accent}55`, background: `${C.accent}0d`, marginBottom: 16 }} data-testid="guided-offer-box">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Sparkles size={16} style={{ color: C.accent }} />
          <span style={{ fontWeight: 700, color: C.accent, fontSize: 14 }}>Dein {discountPct}%-Vorteil ab dem 2. Training pro Woche</span>
        </div>
        <p style={{ color: C.textMuted, fontSize: 13, margin: 0, lineHeight: 1.6 }}>
          1. Training: <strong style={{ color: C.text }}>{currency} {chf(price)}</strong>
          {pts > 0 && <> ({pts} Punkte)</>} · jedes weitere Training pro Woche nur{' '}
          <strong style={{ color: C.accent }}>{currency} {chf(secondPrice)}</strong>
          {pts > 0 && <> ({secondPts} statt {pts} Punkte)</>}.
        </p>
      </div>

      {/* Wochen-/Monatsübersicht */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
        <div style={card} data-testid="guided-weekly">
          <div style={{ color: C.textMuted, fontSize: 12 }}>Pro Woche ({f}×)</div>
          <div style={{ fontFamily: headingFont, fontSize: 26, fontWeight: 800, color: C.text }}>{currency} {chf(weeklyOffer)}</div>
          {weeklySavings > 0 && (
            <div style={{ fontSize: 12, color: C.accent }}>statt {currency} {chf(weeklyFull)} · du sparst {currency} {chf(weeklySavings)}</div>
          )}
        </div>
        <div style={card}>
          <div style={{ color: C.textMuted, fontSize: 12 }}>Pro Monat (ca. 4 Wochen)</div>
          <div style={{ fontFamily: headingFont, fontSize: 26, fontWeight: 800, color: C.text }}>{currency} {chf(monthlyOffer)}</div>
          <div style={{ fontSize: 12, color: C.textMuted }}>{monthlySessions} Trainings{pts > 0 ? ` · ${monthlyPts} Punkte` : ''}</div>
        </div>
      </div>

      {/* Vergleich der Bezahlwege */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 12px' }}>
        <Trophy size={16} style={{ color: C.accent }} />
        <span style={{ fontFamily: headingFont, fontWeight: 700, color: C.text }}>Deine Optionen im Vergleich (pro Monat)</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        {/* Einzelzahlung mit 25% */}
        <div style={{ ...card, borderColor: C.accent, position: 'relative' }} data-testid="guided-opt-single">
          <span style={{ position: 'absolute', top: -10, left: 16, background: C.accent, color: '#000', fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 999, letterSpacing: '0.06em' }}>MIT {discountPct}%-VORTEIL</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Sparkles size={14} style={{ color: C.accent }} />
            <span style={{ fontWeight: 700 }}>Einzeln zahlen</span>
          </div>
          <div style={{ fontFamily: headingFont, fontSize: 22, fontWeight: 800 }}>{currency} {chf(monthlyOffer)}</div>
          <div style={{ color: C.textMuted, fontSize: 12, marginTop: 4 }}>≈ {currency} {chf(monthlyOffer / monthlySessions)} pro Training</div>
        </div>

        {/* Service-Abo Mengenrabatt */}
        {serviceAboTiers.length > 0 && (
          <div style={card} data-testid="guided-opt-abo">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <Package size={14} style={{ color: C.accent }} />
              <span style={{ fontWeight: 700 }}>{monthlySessions}er-Abo{abo.label ? ` · ${abo.label}` : ''}</span>
            </div>
            <div style={{ fontFamily: headingFont, fontSize: 22, fontWeight: 800 }}>{currency} {chf(abo.total)}</div>
            <div style={{ color: C.textMuted, fontSize: 12, marginTop: 4 }}>
              {abo.pct > 0 ? `${chf(abo.pct)}% Mengenrabatt · ` : ''}≈ {currency} {chf(abo.perSession)} pro Training
            </div>
            <button style={{ ...cta(false), marginTop: 12 }} onClick={() => onBuyPackage && onBuyPackage({ type: 'service_abo', id: service.id, name: `${monthlySessions}× ${service.name}`, price: abo.total })} data-testid="guided-buy-abo">
              Abo sichern <ArrowRight size={14} />
            </button>
          </div>
        )}

        {/* Punkte-Paket Bonus */}
        {pts > 0 && pointTiers.length > 0 && (
          <div style={card} data-testid="guided-opt-points">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <Star size={14} style={{ color: C.accent }} />
              <span style={{ fontWeight: 700 }}>Punkte-Paket{pTier?.label ? ` · ${pTier.label}` : ''}</span>
            </div>
            <div style={{ fontFamily: headingFont, fontSize: 22, fontWeight: 800 }}>{currency} {chf(pointsFullCHF)}</div>
            <div style={{ color: C.textMuted, fontSize: 12, marginTop: 4 }}>
              {monthlyPts} Punkte{bonusPoints > 0 ? <> · <strong style={{ color: C.accent }}>+{bonusPoints} gratis</strong> (Wert {currency} {chf(pointsBonusValue)})</> : ' · noch kein Bonus'}
            </div>
            <button style={{ ...cta(false), marginTop: 12 }} onClick={() => onBuyPackage && onBuyPackage({ type: 'points', id: 'points', name: `${monthlyPts} Punkte`, price: pointsFullCHF, points: monthlyPts })} data-testid="guided-buy-points">
              Punkte kaufen <ArrowRight size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Sparziel-Hinweis */}
      <div style={{ ...card, marginTop: 16, borderColor: `${C.accent}55`, background: `${C.accent}0d` }} data-testid="guided-savings-hint">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <TrendingDown size={16} style={{ color: C.accent }} />
          <span style={{ fontWeight: 700, color: C.text, fontSize: 14 }}>Wann lohnt sich ein Abo?</span>
        </div>
        <p style={{ color: C.textMuted, fontSize: 13, margin: 0, lineHeight: 1.6 }}>
          {aboThreshold
            ? <>Ab <strong style={{ color: C.accent }}>{aboThreshold}× pro Woche</strong> zahlst du mit einem Mengen-Abo weniger als bei Einzelzahlung – trotz deines {discountPct}%-Vorteils ab dem 2. Training.</>
            : <>Dank deines <strong style={{ color: C.accent }}>{discountPct}%-Vorteils</strong> ab dem 2. Training pro Woche zahlst du <strong style={{ color: C.text }}>einzeln</strong> hier am wenigsten – ein Abo bringt vor allem Planungssicherheit. {pts > 0 && bonusPoints > 0 && <>Mit einem grösseren Punkte-Paket bekommst du zusätzlich {bonusPoints} Gratis-Punkte.</>}</>}
        </p>
      </div>

      {/* Abschluss-CTAs */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 20 }}>
        <button style={{ ...cta(true), flex: 1, minWidth: 200 }} onClick={() => onBook && onBook(service)} data-testid="guided-book">
          <Check size={15} /> Kostenlose Probesession buchen
        </button>
        <button style={{ ...cta(false), width: 'auto', padding: '13px 18px' }} onClick={reset} data-testid="guided-restart">
          <RotateCcw size={14} /> Neu berechnen
        </button>
      </div>
    </div>
  );
};

export default GuidedPricing;
